/**
 * One body paragraph of a document as the web route `document-paragraphs`
 * reports it to the core: everything the core's heading detection, LLM
 * locate and edit prompts need to know about a paragraph without parsing
 * OOXML themselves.
 *
 * `allChangesAcceptedText` is the paragraph with every tracked change accepted,
 * `allChangesRejectedText` the paragraph with every tracked change rejected; the two
 * layers let the locator find a paragraph whose text was struck as well as
 * one whose text is live. `trackedChangesText` is the paragraph with every
 * tracked change still visible, insertions wrapped in `<ins author="…">` and
 * deletions in `<del author="…">`; it is the text the core shows the edit
 * model. `numberingLabel` is the list label Word paints before the paragraph,
 * computed from the numbering and styles parts, so the agent reads clause
 * numbers as the document shows them. `leadingClauseMarker` is the typed
 * clause number at the start of the accepted text, which is how the core
 * recognises a numbered heading in a flat OPC that carries no rendered
 * numbering. `outlineLevel` and `listLevel` are what Word itself says about the
 * paragraph's place in the document's structure — the heading depth its style
 * gives it (`w:outlineLvl`) and the list level it is numbered at
 * (`w:numPr/w:ilvl`) — so the core's heading map can read the document's own
 * structure instead of inferring one from the shape of the typed text.
 * `paragraphBookmarkName` is the paragraph's id, the one bookmark
 * that names it, so the core addresses a paragraph without knowing what a
 * paragraph bookmark looks like; `bookmarkNames` beside it lists every
 * bookmark the paragraph answers to, that id included. `comments` lists the Word
 * comments anchored in the paragraph, each with the durable id the core's
 * reply tool takes, when it was written and which comment it answers, so the
 * agent's view of the document and its comment tools read from this list and
 * never from the file themselves. `pendingChanges` lists the tracked changes
 * `trackedChangesText` tags, by the same ids, each with its author, date, words
 * and place in the accepted text, and `formattingChanges` the tracked
 * formatting changes, so the core can lay out who edited what and when
 * without parsing the file. `uneditableSpans`
 * lists the stretches of `allChangesAcceptedText` Word regenerates from its own markup
 * (field results such as cross-references and page numbers, field instructions,
 * equations), so a prompt can mark them and the apply route can hand back an
 * anchor that crosses one instead of the engine asking again for the whole write later.
 * `tableOfContentsEntry` is set for a paragraph inside a table of contents
 * field, which Word rewrites from the headings whenever the field updates, and
 * names the heading the entry lists, so the apply route moves an edit aimed at
 * the entry onto that heading instead of writing into text Word will discard.
 * `tableCellPath` places the paragraph inside its table cells, outermost first,
 * so the table operations can name a row by any paragraph of it and the prompts
 * can label a cell paragraph as a cell. `headingPath` is the chain of headings
 * the paragraph sits under (`DocumentHeadings`), built here once so the add-in's
 * recovery of a lost card, the web's paragraph locator and the core's card
 * anchors all read the same path.
 *
 * The request names the document by reference (`DocumentReference`) so the
 * web loads it from where it already lives instead of receiving its bytes.
 */
import { z } from "zod";

import { documentReferenceSchema } from "./document-reference";
import { tableCellPathSchema } from "./table-cell-path";

export const documentCommentSchema = z.object({
    /** The durable comment id as a decimal string, or the `w:id` reference when the file carries no durable id. */
    id: z.string(),
    author: z.string(),
    text: z.string(),
    /**
     * When the comment was written: its `w:date` as Word stores it (ISO 8601).
     *
     * NULL
     *   The file carries no date for it (personal information removed), or the
     *   paragraphs came from a web deployed before this field.
     */
    date: z.string().nullable().default(null),
    /**
     * The `id` of the comment this one replies to, read from the file's commentsExtended part.
     *
     * NULL
     *   A comment that starts a thread, or paragraphs from a web deployed before this field.
     */
    repliesTo: z.string().nullable().default(null),
    /**
     * True when the comment's thread is resolved in Word (`DocumentComments`).
     *
     * WHEN a record arrives without it
     *   CONSEQUENCE
     *     the comment reads as open.
     *
     * WHY a default
     *   The web, the core and the add-in deploy apart: a core or web deployed
     *   before this field sends comment records without it, and those must
     *   still parse during the rollout.
     */
    resolved: z.boolean().default(false),
});

export type DocumentComment = z.infer<typeof documentCommentSchema>;

export const paragraphPendingChangeSchema = z.object({
    /** The id `trackedChangesText` tags the change with ("c1", "c2", …), so a reader of this list names the change the agent's view shows. */
    id: z.string(),
    /** Words an author added (`ins`) or struck (`del`). */
    kind: z.enum(["ins", "del"]),
    author: z.string(),
    /**
     * When the change was made: its revision's `w:date` (ISO 8601).
     *
     * NULL
     *   The file carries no date for it (personal information removed).
     */
    date: z.string().nullable(),
    /** The change's words: an insertion's whole text, the words someone later struck inside it included; a deletion's struck words. */
    text: z.string(),
    /** Offset into `allChangesAcceptedText` where the change sits; a deletion claims no accepted characters, so its two offsets are equal. */
    startOffset: z.number().int().nonnegative(),
    /** Offset one past the change's last accepted character, so `allChangesAcceptedText.slice(startOffset, endOffset)` is what the change left live. */
    endOffset: z.number().int().nonnegative(),
});

export type ParagraphPendingChange = z.infer<typeof paragraphPendingChangeSchema>;

export const paragraphFormattingChangeSchema = z.object({
    author: z.string(),
    /**
     * When the formatting was changed: the `w:rPrChange`'s `w:date` (ISO 8601).
     *
     * NULL
     *   The file carries no date for it (personal information removed).
     */
    date: z.string().nullable(),
    /** The live words whose formatting changed. */
    text: z.string(),
    /** Offset into `allChangesAcceptedText` of the first changed character. */
    startOffset: z.number().int().nonnegative(),
    /** Offset one past the last changed character, so `allChangesAcceptedText.slice(startOffset, endOffset) === text`. */
    endOffset: z.number().int().nonnegative(),
});

export type ParagraphFormattingChange = z.infer<typeof paragraphFormattingChangeSchema>;

export const uneditableSpanSchema = z.object({
    /** Offset into `allChangesAcceptedText` of the span's first character. */
    startOffset: z.number().int().nonnegative(),
    /** Offset one past the span's last character, so `allChangesAcceptedText.slice(startOffset, endOffset)` is the span. */
    endOffset: z.number().int().nonnegative(),
    /** The span as `allChangesAcceptedText` reads it, so a prompt shows it without slicing. */
    text: z.string(),
});

export type UneditableSpan = z.infer<typeof uneditableSpanSchema>;

/**
 * One link of a paragraph (`ParagraphLinks.readingOf`): where its display text sits in
 * `allChangesAcceptedText`, that text, and the address it points to, shown to the agent
 * and the edit model so an edit over the link says what becomes of it.
 */
export const paragraphLinkSchema = z.object({
    /** Offset into `allChangesAcceptedText` of the display text's first character. */
    startOffset: z.number().int().nonnegative(),
    /** Offset one past its last character, so `allChangesAcceptedText.slice(startOffset, endOffset)` is `displayText`. */
    endOffset: z.number().int().nonnegative(),
    displayText: z.string(),
    /**
     * The target, then "#" and the place inside it where the link names one (`linkAddressOf`).
     *
     * NULL
     *   The address is not known: a `w:hyperlink` whose relationship the package does
     *   not hold, or a HYPERLINK field that names no target.
     */
    address: z.string().nullable(),
});

export type ParagraphLink = z.infer<typeof paragraphLinkSchema>;

export const tableOfContentsEntrySchema = z.object({
    /**
     * The paragraph bookmark of the heading this entry lists, so an operation aimed at
     * the entry can be moved to the heading.
     *
     * NULL
     *   The entry points at no heading the reader can name: its anchor is on no
     *   paragraph of the body, or that paragraph carries no paragraph bookmark yet.
     */
    targetParagraphBookmarkName: z.string().nullable(),
    /**
     * How the entry is kept in step with its heading.
     *
     * CASES
     *   - "field"       -> Word rewrites the entry from the heading whenever the table updates
     *   - "linked_list" -> the entry is a typed line linking to its heading; nothing rewrites
     *                      it, so an edit on the heading is copied onto it
     *
     * FALLBACK
     *   "field" when absent: a core deployed before this field echoes the paragraphs
     *   it read without it, and those entries were all inside a `TOC` field.
     */
    listedBy: z.enum(["field", "linked_list"]).default("field"),
});

export type TableOfContentsEntry = z.infer<typeof tableOfContentsEntrySchema>;

export const documentParagraphSchema = z.object({
    paragraphIndex: z.number().int().nonnegative(),
    allChangesAcceptedText: z.string(),
    allChangesRejectedText: z.string(),
    trackedChangesText: z.string(),
    /** `trackedChangesText` plus the agent view's `<strike>`, `<u>` and `<image/>` markers: what the focused-document tool shows the agent. */
    agentViewText: z.string(),
    /** True when Word numbers the paragraph itself (`w:pPr/w:numPr`), so a typed number in edited text would render twice. */
    isAutoNumbered: z.boolean(),
    /**
     * The list label Word paints before the paragraph ("17.6", "(b)", "•"), computed from the
     * document's numbering definitions; null for a paragraph Word does not number. A label, never
     * an id: paragraphs are addressed by `paragraphBookmarkName` only.
     */
    numberingLabel: z.string().nullable(),
    /**
     * Word's own heading depth for the paragraph (`w:outlineLvl`, its own or inherited
     * through its style chain), 0 for a `Heading 1`; null when the paragraph is body
     * text. The one signal that says a line is a heading rather than a line that
     * happens to start with a number, so the heading builder prefers it.
     */
    outlineLevel: z.number().int().nonnegative().nullable(),
    /**
     * The list level the paragraph is numbered at (`w:numPr/w:ilvl`), 0 for a top-level
     * item; null for a paragraph Word does not number. The depth behind `numberingLabel`,
     * stated rather than read back out of the painted label.
     */
    listLevel: z.number().int().nonnegative().nullable(),
    paraId: z.string().nullable(),
    textId: z.string().nullable(),
    bookmarkNames: z.array(z.string()),
    /**
     * The bookmark that names this paragraph for the engine, whatever shape the
     * naming in force mints, so no reader of this wire has to recognise one.
     *
     * NULL
     *   The paragraph owns no paragraph bookmark: a raw upload the naming pass
     *   has not reached, or a paragraph a write inserted since the last pass. It
     *   is still listed, with its text, and no operation can name it until a
     *   pass gives it one.
     */
    paragraphBookmarkName: z.string().nullable(),
    leadingClauseMarker: z.string().nullable(),
    /** The Word comments anchored in this paragraph, in marker order. */
    comments: z.array(documentCommentSchema),
    /**
     * The paragraph's pending tracked changes in the order `trackedChangesText` numbers
     * them, each with its author, date and words. A change that holds no words (an
     * inserted or struck picture) is left out.
     *
     * FALLBACK
     *   [] when absent: a web deployed before this field listed none.
     */
    pendingChanges: z.array(paragraphPendingChangeSchema).default([]),
    /**
     * The paragraph's tracked formatting changes, in document order.
     *
     * FALLBACK
     *   [] when absent: a web deployed before this field listed none.
     */
    formattingChanges: z.array(paragraphFormattingChangeSchema).default([]),
    /** The stretches of `allChangesAcceptedText` Word regenerates from its own markup, in text order; an edit inside one is handed back to the model. */
    uneditableSpans: z.array(uneditableSpanSchema),
    /**
     * The paragraph's links, in the order the reader walks their spellings (`ParagraphLinks`).
     *
     * EMPTY
     *   [] for a paragraph with no link, and for a table of contents entry, whose link to
     *   its heading is Word's. A core deployed before this field echoes the paragraphs it
     *   read without it, and they read as holding no link.
     */
    links: z.array(paragraphLinkSchema).default([]),
    /** Set for a paragraph inside a table of contents field (`TableOfContents`), naming the heading it lists; null for every other paragraph. */
    tableOfContentsEntry: tableOfContentsEntrySchema.nullable(),
    /** The table cells the paragraph sits in, outermost first (`DocumentTableCell`); empty for a body paragraph outside every table. */
    tableCellPath: tableCellPathSchema,
    /**
     * The headings the paragraph sits under, part first then shallowest clause first
     * (`["Main Agreement", "4. Payment", "4.2 Invoicing"]`), as `DocumentHeadings` builds
     * them from the outline levels, the typed and painted clause numbers and the named
     * divisions.
     *
     * NULL
     *   No heading stands over the paragraph yet: a title, the parties, a recital.
     */
    headingPath: z.array(z.string()).nullable(),
});

export type DocumentParagraph = z.infer<typeof documentParagraphSchema>;

export const documentParagraphsRequestSchema = z.object({
    document: documentReferenceSchema,
});

export type DocumentParagraphsRequest = z.infer<typeof documentParagraphsRequestSchema>;
